import React, { useState } from 'react';
import BoxedIcon from './boxed-icon';
import SharedButton from './buttons/shared-button';
import {
  Condition,
  createConditionBlock,
  createDefaultCondition,
  FieldType,
  fieldOptions,
  groupQueryString,
  isCondition,
  isRuleGroup,
  LogicalOperator,
  RuleGroup,
  valueOptionsMap,
} from './query-builder-utils';

import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import Tooltip from '@mui/material/Tooltip';
import { useTheme } from '@mui/material/styles';
import Add from '@mui/icons-material/Add';
import Block from '@mui/icons-material/Block';
import DeleteOutline from '@mui/icons-material/DeleteOutline';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import AddCircleOutline from '@mui/icons-material/AddCircleOutline';

function AndOrToggle({
  value,
  onChange,
  disabled,
  andAriaLabel,
  orAriaLabel,
}: {
  value: LogicalOperator;
  onChange: (logic: LogicalOperator) => void;
  disabled?: boolean;
  andAriaLabel: string;
  orAriaLabel: string;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      <SharedButton
        variant={value !== 'OR' ? 'primary' : 'secondary'}
        size="small"
        disabled={disabled}
        aria-label={andAriaLabel}
        onClick={() => onChange('AND')}
        sx={{ minWidth: '50px', textTransform: 'none' }}
      >
        And
      </SharedButton>
      <SharedButton
        variant={value === 'OR' ? 'primary' : 'secondary'}
        size="small"
        disabled={disabled}
        aria-label={orAriaLabel}
        onClick={() => onChange('OR')}
        sx={{ minWidth: '50px', textTransform: 'none' }}
      >
        Or
      </SharedButton>
    </Box>
  );
}

function ConditionRow({
  condition,
  disabled,
  onFieldChange,
  onValueChange,
  onDelete,
}: {
  condition: Condition;
  disabled?: boolean;
  onFieldChange: (field: FieldType) => void;
  onValueChange: (value: string) => void;
  onDelete: () => void;
}) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        pl: 2,
        borderLeft: '2px solid',
        borderLeftColor: 'primary.main',
      }}
    >
      <Box flex={1}>
        <Select
          value={condition.field}
          fullWidth
          size="small"
          disabled={disabled}
          onChange={(e) => onFieldChange(e.target.value as FieldType)}
          inputProps={{ 'aria-label': 'Condition field' }}
        >
          {fieldOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <Select
        disabled
        value="Equals"
        size="small"
        inputProps={{ 'aria-label': 'Condition operator' }}
      >
        <MenuItem value="Equals">equals</MenuItem>
      </Select>
      <Box flex={1}>
        <Select
          value={condition.value}
          fullWidth
          size="small"
          disabled={disabled}
          onChange={(e) => onValueChange(e.target.value)}
          inputProps={{ 'aria-label': 'Condition value' }}
        >
          {(valueOptionsMap[condition.field] || []).map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </Box>
      <IconButton
        size="small"
        onClick={onDelete}
        aria-label="Delete condition"
        disabled={disabled}
      >
        <DeleteOutline fontSize="inherit" />
      </IconButton>
    </Box>
  );
}

function ConditionBlockCard({
  ruleGroup,
  onRuleGroupChange,
  onRequestDelete,
  disabled,
  collapsed,
  onToggleCollapse,
}: {
  ruleGroup: RuleGroup;
  onRuleGroupChange: (updatedGroup: RuleGroup) => void;
  onRequestDelete: () => void;
  disabled?: boolean;
  collapsed: boolean;
  onToggleCollapse: () => void;
}) {
  const theme = useTheme();
  const conditions = ruleGroup.rules.filter(isCondition);
  const blockQuery = groupQueryString({ ...ruleGroup, rules: conditions });

  const handleLogicChange = (newLogic: LogicalOperator) => {
    onRuleGroupChange({ ...ruleGroup, logic: newLogic });
  };

  const handleConditionFieldChange = (ruleId: string, newField: FieldType) => {
    const newRules = ruleGroup.rules.map((rule) => {
      if (rule.id === ruleId && isCondition(rule)) {
        return {
          ...rule,
          field: newField,
          value: valueOptionsMap[newField][0].value,
        };
      }
      return rule;
    });
    onRuleGroupChange({ ...ruleGroup, rules: newRules });
  };

  const handleConditionValueChange = (ruleId: string, newValue: string) => {
    const newRules = ruleGroup.rules.map((rule) => {
      if (rule.id === ruleId && isCondition(rule)) {
        return { ...rule, value: newValue };
      }
      return rule;
    });
    onRuleGroupChange({ ...ruleGroup, rules: newRules });
  };

  const handleDeleteCondition = (itemId: string) => {
    onRuleGroupChange({
      ...ruleGroup,
      rules: ruleGroup.rules.filter((rule) => rule.id !== itemId),
    });
  };

  const addCondition = () => {
    onRuleGroupChange({
      ...ruleGroup,
      rules: [...ruleGroup.rules, createDefaultCondition()],
    });
  };

  return (
    <Card
      variant="outlined"
      data-testid={`rule-group-${ruleGroup.id}`}
      sx={{
        backgroundColor:
          theme.palette.mode === 'dark' ? 'background.default' : 'grey.50',
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Conditions Match
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Tooltip title={collapsed ? 'Expand block' : 'Collapse block'}>
              <IconButton
                size="small"
                onClick={onToggleCollapse}
                aria-label={collapsed ? 'Expand block' : 'Collapse block'}
              >
                {collapsed ? <ExpandMore /> : <ExpandLess />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete block">
              <IconButton
                size="small"
                onClick={onRequestDelete}
                disabled={disabled}
                aria-label="Delete block"
              >
                <DeleteOutline />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      </CardContent>
      <Collapse in={!collapsed}>
        <Box
          sx={{
            backgroundColor:
              theme.palette.colors?.info?.[100] || theme.palette.grey[200],
            width: '100%',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              p: 1,
              display: 'block',
              fontStyle: 'italic',
              fontFamily: blockQuery ? 'monospace' : undefined,
              color:
                theme.palette.colors?.neutral?.[700] ||
                theme.palette.text.secondary,
            }}
          >
            {blockQuery || 'Add a condition to see your filter logic here.'}
          </Typography>
        </Box>
        <CardContent sx={{ p: 2 }}>
          <Stack gap={2}>
            {conditions.map((condition, conditionIndex) => (
              <Box key={condition.id}>
                {conditionIndex > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                    <AndOrToggle
                      value={ruleGroup.logic}
                      onChange={handleLogicChange}
                      disabled={disabled}
                      andAriaLabel="Combine conditions with And"
                      orAriaLabel="Combine conditions with Or"
                    />
                  </Box>
                )}
                <ConditionRow
                  condition={condition}
                  disabled={disabled}
                  onFieldChange={(field) =>
                    handleConditionFieldChange(condition.id, field)
                  }
                  onValueChange={(value) =>
                    handleConditionValueChange(condition.id, value)
                  }
                  onDelete={() => handleDeleteCondition(condition.id)}
                />
              </Box>
            ))}
            <Box>
              <SharedButton
                startIcon={<Add />}
                size="small"
                variant="secondary"
                disabled={disabled}
                onClick={addCondition}
                sx={{ textTransform: 'none' }}
              >
                Add condition
              </SharedButton>
            </Box>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
}

export const QueryBuilder: React.FC<{
  ruleGroup: RuleGroup;
  onRuleGroupChange: (updatedGroup: RuleGroup) => void;
  onRequestDelete?: () => void;
  depth?: number;
  disabled?: boolean;
  description?: string;
}> = ({
  ruleGroup,
  onRuleGroupChange,
  disabled,
  description = 'Select whether you want to restrict who can be members of this group.',
}) => {
  const theme = useTheme();
  const [collapsedBlocks, setCollapsedBlocks] = useState<
    Record<string, boolean>
  >({});
  const blocks = ruleGroup.rules.filter(isRuleGroup);
  const hasBlocks = blocks.length > 0;
  const fullQuery = groupQueryString(ruleGroup);

  const handleLogicChange = (newLogic: LogicalOperator) => {
    onRuleGroupChange({ ...ruleGroup, logic: newLogic });
  };

  const handleDeleteBlock = (itemId: string) => {
    onRuleGroupChange({
      ...ruleGroup,
      rules: ruleGroup.rules.filter((rule) => rule.id !== itemId),
    });
  };

  const addBlock = () => {
    onRuleGroupChange({
      ...ruleGroup,
      rules: [...ruleGroup.rules, createConditionBlock()],
    });
  };

  const handleSubgroupChange = (
    subgroupId: string,
    updatedSubgroup: RuleGroup
  ) => {
    const newRules = ruleGroup.rules.map((rule) => {
      if (rule.id === subgroupId && isRuleGroup(rule)) {
        return updatedSubgroup;
      }
      return rule;
    });
    onRuleGroupChange({ ...ruleGroup, rules: newRules });
  };

  return (
    <Card
      variant="outlined"
      data-testid={`rule-group-${ruleGroup.id}`}
      sx={{ mt: 2 }}
    >
      <CardContent sx={{ p: 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <BoxedIcon icon={Block} variant="rose" size={36} />
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                Member Restrictions
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            </Box>
          </Box>
          {hasBlocks && (
            <AndOrToggle
              value={ruleGroup.logic}
              onChange={handleLogicChange}
              disabled={disabled}
              andAriaLabel="Combine blocks with And"
              orAriaLabel="Combine blocks with Or"
            />
          )}
        </Box>
      </CardContent>
      <Divider />
      <CardContent sx={{ p: 2 }}>
        {!hasBlocks ? (
          <Box
            sx={{
              border: `1px dashed ${theme.palette.semantic.outline.light}`,
              borderRadius: 1,
              py: 4,
              px: 2,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 1,
              color: theme.palette.semantic.text.title2,
            }}
          >
            <AddCircleOutline aria-hidden fontSize="small" />
            <Typography variant="body2">No restrictions added.</Typography>
            <SharedButton
              startIcon={<Add />}
              variant="secondary"
              size="small"
              disabled={disabled}
              onClick={addBlock}
              sx={{ textTransform: 'none' }}
            >
              Add Restriction
            </SharedButton>
          </Box>
        ) : (
          <Stack gap={1} width="100%">
            {blocks.map((block, blockIndex) => (
              <Box key={block.id}>
                {blockIndex > 0 && (
                  <Divider sx={{ my: 2 }}>
                    <Typography
                      variant="button"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontWeight: 700,
                        textTransform: 'none',
                      }}
                    >
                      {ruleGroup.logic === 'AND' ? 'And' : 'Or'}
                    </Typography>
                  </Divider>
                )}
                <ConditionBlockCard
                  ruleGroup={block}
                  onRuleGroupChange={(updated) =>
                    handleSubgroupChange(block.id, updated)
                  }
                  onRequestDelete={() => handleDeleteBlock(block.id)}
                  disabled={disabled}
                  collapsed={Boolean(collapsedBlocks[block.id])}
                  onToggleCollapse={() =>
                    setCollapsedBlocks((prev) => ({
                      ...prev,
                      [block.id]: !prev[block.id],
                    }))
                  }
                />
              </Box>
            ))}
            {fullQuery && (
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  color: theme.palette.text.secondary,
                  display: 'block',
                  pt: 1,
                }}
              >
                {fullQuery}
              </Typography>
            )}
            <SharedButton
              startIcon={<Add />}
              variant="secondary"
              size="small"
              disabled={disabled}
              onClick={addBlock}
              sx={{ alignSelf: 'flex-start', mt: 1, textTransform: 'none' }}
            >
              Add Condition Block
            </SharedButton>
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
